import React, { useEffect, useMemo, useState } from 'react';
import { getMe, getLimits, setProfile, uploadAvatar, requestPasswordRecovery, requestDataExport, updateUserPreferences, logout } from '../api/endpoints';
import { clearAuthToken } from '../api/config';
import { useAuthImage } from '../hooks/useAuthImage';
import Loading from '../components/Loading';
import { navigateTo } from '../utils/navigation';
import { motion, AnimatePresence, useReducedMotion, type Variants, type Transition } from 'framer-motion';
import Cropper, { type Area } from 'react-easy-crop';
import { getCroppedImageBlob } from '../utils/cropImage';
import { readAffiliateChoice, writeAffiliateChoice } from '../utils/affiliateConsent';
import ConfirmationDialog from '../components/ui/ConfirmationDialog';
import {
  NAME_RE,
  COUNTRY_RE,
  CITY_RE,
  GENDER_OTHER_RE,
  isValidISODate,
  type GenderOption,
} from '../utils/regex';
import Toast from '../components/ui/Toast';
import NotificationSettings from '../components/NotificationSettings';
import { type Credits, type User } from '../types/api';
import { getErrorMessage } from '../utils/errors';

interface ProfileUser extends User {
  avatar?: string;
  photo_ai_consent?: boolean;
  newsletter_opt_in?: boolean;
  created_at?: string;
}

type ProfileFormData = Pick<
  ProfileUser,
  'name' | 'surname' | 'country' | 'city' | 'birthdate' | 'gender' | 'avatar'
>;

function getApiErrorMessage(error: unknown, fallback: string): string {
  const message = getErrorMessage(error, fallback);
  if (message !== fallback) return message;
  if (typeof error === 'object' && error !== null) {
    const record = error as Record<string, unknown>;
    for (const key of ['message', 'error', 'detail'] as const) {
      const val = record[key];
      if (typeof val === 'string' && val.length > 0) return val;
    }
  }
  return fallback;
}

function formatYMD(raw: string): string {
  const s = raw.replace(/[^\d]/g, '').slice(0, 8);
  const y = s.slice(0, 4);
  const m = s.slice(4, 6);
  const d = s.slice(6, 8);
  return [y, m && `-${m}`, d && `-${d}`].filter(Boolean).join('');
}

function normalizeDateRFC3339(dateStr?: string): string {
  if (!dateStr || dateStr === '0001-01-01T00:00:00Z') return '0001-01-01T00:00:00Z';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '0001-01-01T00:00:00Z';
  return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())).toISOString();
}

function initGenderFromString(
  g: string,
  setGenderOption: (value: GenderOption) => void,
  setGenderOtherText: (value: string) => void,
): void {
  const base = g.trim();
  if (base === '' || base === 'Female' || base === 'Male' || base === 'Prefer not to say') {
    setGenderOption(base);
    setGenderOtherText('');
  } else {
    setGenderOption('Other');
    setGenderOtherText(base);
  }
}

function resolveAvatarPath(user: ProfileUser | null): string | null {
  const avatar = user?.avatar ?? user?.avatar_path;
  return avatar ? avatar.replace(/^\.\/+/, '') : null;
}

function displayOptional(value?: string): string {
  return value && value.length > 0 ? value : '—';
}

async function handleLogout(): Promise<void> {
  try { await logout(); } catch { /* best-effort */ }
  clearAuthToken();
  navigateTo('/login');
}

export default function ProfilePage(): React.JSX.Element {
  const [user, setUser] = useState<ProfileUser | null>(null);
  const [limits, setLimits] = useState<Credits | null>(null);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState<ProfileFormData>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>('');

  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const [privacyConfirmOpen, setPrivacyConfirmOpen] = useState(false);
  const [privacyConfirmField, setPrivacyConfirmField] = useState<'photo_ai_consent' | 'newsletter_opt_in' | null>(null);
  const [photoAiConsent, setPhotoAiConsent] = useState(false);
  const [newsletterOptIn, setNewsletterOptIn] = useState(false);
  const [affiliateRecs, setAffiliateRecs] = useState(() => readAffiliateChoice() === 'on');

  const handleAffiliateToggle = (value: boolean) => {
    setAffiliateRecs(value);
    writeAffiliateChoice(value ? 'on' : 'off');
  };

  const todayISO = (() => {
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  })();

  const [toast, setToast] = useState<{
    open: boolean;
    variant: 'success' | 'error' | 'info';
    title: string;
    description?: string;
  }>({ open: false, variant: 'info', title: '' });

  const isIOS = /iP(hone|od|ad)/.test(navigator.userAgent);

  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarCropSrc, setAvatarCropSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState<number>(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

  const [genderOption, setGenderOption] = useState<GenderOption>('');
  const [genderOtherText, setGenderOtherText] = useState('');

  const [touched, setTouched] = useState({
    name: false,
    surname: false,
    country: false,
    city: false,
    birthdate: false,
    genderOther: false,
  });

  const prefersReduced = useReducedMotion();
  const spring: Transition = prefersReduced
    ? { duration: 0 }
    : { type: 'spring', stiffness: 420, damping: 36, mass: 0.9 };
  const quick: Transition = prefersReduced ? { duration: 0 } : { duration: 0.18 };

  const pageIn: Variants = {
    hidden: { opacity: 0, y: 8 },
    visible: { opacity: 1, y: 0, transition: spring },
  };
  const listContainer: Variants = {
    hidden: {},
    visible: { transition: { staggerChildren: prefersReduced ? 0 : 0.06, delayChildren: prefersReduced ? 0 : 0.04 } },
  };
  const listItem: Variants = {
    hidden: { opacity: 0, y: 6 },
    visible: { opacity: 1, y: 0, transition: spring },
  };

  useEffect(() => {
    void Promise.all([
      getMe().then((u) => {
        const profileUser = u as ProfileUser;
        setUser(profileUser);
        setFormData(profileUser);
        initGenderFromString(profileUser.gender ?? '', setGenderOption, setGenderOtherText);
        setPhotoAiConsent(profileUser.photo_ai_consent ?? false);
        setNewsletterOptIn(profileUser.newsletter_opt_in ?? false);
      }),
      getLimits().then(setLimits),
    ]).catch((error_: unknown) => {
      console.error(error_);
      setError('Failed to load profile.');
    });
  }, []);

  useEffect(() => {
    return () => {
      if (avatarCropSrc) URL.revokeObjectURL(avatarCropSrc);
    };
  }, [avatarCropSrc]);

  const { src: currentAvatarSrc } = useAuthImage(resolveAvatarPath(user));

  const avatarPreview = useMemo(() => {
    if (avatarCropSrc) return avatarCropSrc;
    return currentAvatarSrc ?? '';
  }, [avatarCropSrc, currentAvatarSrc]);

  if (!user || !limits) return <Loading />;

  const birthdateInput: string =
    formData.birthdate && formData.birthdate !== '0001-01-01T00:00:00Z'
      ? formData.birthdate.split('T')[0]
      : '';

  const nameValid = !formData.name || NAME_RE.test(formData.name.trim());
  const surnameValid = !formData.surname || NAME_RE.test(formData.surname.trim());
  const countryValid = !formData.country || COUNTRY_RE.test(formData.country.trim());
  const cityValid = !formData.city || CITY_RE.test(formData.city.trim());
  const birthdateValid = !birthdateInput || isValidISODate(birthdateInput);
  const genderOtherValid =
    genderOption !== 'Other' ||
    genderOtherText.trim() === '' ||
    GENDER_OTHER_RE.test(genderOtherText.trim());

  const showNameErr = touched.name && !!formData.name && !nameValid;
  const showSurnameErr = touched.surname && !!formData.surname && !surnameValid;
  const showCountryErr = touched.country && !!formData.country && !countryValid;
  const showCityErr = touched.city && !!formData.city && !cityValid;
  const showDobErr = touched.birthdate && !!birthdateInput && !birthdateValid;
  const showGenderOtherErr =
    genderOption === 'Other' &&
    touched.genderOther &&
    genderOtherText.trim().length > 0 &&
    !genderOtherValid;

  const allValid =
    nameValid && surnameValid && countryValid && cityValid && birthdateValid && genderOtherValid;

  const resolvedGender = genderOption === 'Other' ? genderOtherText.trim() : genderOption;

  const handlePrivacyToggle = async (field: 'photo_ai_consent' | 'newsletter_opt_in', value: boolean) => {
    if (!value) {
      setPrivacyConfirmField(field);
      setPrivacyConfirmOpen(true);
      return;
    }
    try {
      await updateUserPreferences({ [field]: true });
      if (field === 'photo_ai_consent') setPhotoAiConsent(true);
      else setNewsletterOptIn(true);
    } catch (error_: unknown) {
      setToast({
        open: true,
        variant: 'error',
        title: 'Failed to update preference',
        description: getErrorMessage(error_, 'Failed to update preference'),
      });
    }
  };

  const handlePrivacyConfirm = async () => {
    if (!privacyConfirmField) return;
    try {
      await updateUserPreferences({ [privacyConfirmField]: false });
      if (privacyConfirmField === 'photo_ai_consent') setPhotoAiConsent(false);
      else setNewsletterOptIn(false);
    } catch (error_: unknown) {
      setToast({
        open: true,
        variant: 'error',
        title: 'Failed to update preference',
        description: getErrorMessage(error_, 'Failed to update preference'),
      });
    } finally {
      setPrivacyConfirmOpen(false);
      setPrivacyConfirmField(null);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleResetPassword = async () => {
    try {
      await requestPasswordRecovery(user.email);
      setToast({
        open: true,
        variant: 'success',
        title: 'Reset link sent',
        description: "If this email is associated with an account, you'll receive a password reset link shortly.",
      });
    } catch (error_: unknown) {
      setToast({
        open: true,
        variant: 'error',
        title: 'Could not send reset email',
        description: getErrorMessage(error_, 'Please try again in a moment.'),
      });
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setTouched((t) => ({
      ...t,
      name: true, surname: true, country: true, city: true, birthdate: true, genderOther: true,
    }));
    if (!allValid) { setSaving(false); return; }

    try {
      await setProfile({
        name: formData.name ?? '',
        surname: formData.surname ?? '',
        country: formData.country ?? '',
        city: formData.city ?? '',
        gender: resolvedGender,
        birthdate: normalizeDateRFC3339(birthdateInput),
      });
      const freshUser = await getMe() as ProfileUser;
      setUser(freshUser);
      setFormData(freshUser);
      initGenderFromString(freshUser.gender ?? '', setGenderOption, setGenderOtherText);
      setEditing(false);
    } catch (error_: unknown) {
      console.error(error_);
      setError(getApiErrorMessage(error_, 'Could not save profile.'));
    } finally {
      setSaving(false);
    }
  };

  const onAvatarPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setAvatarFile(file);
    setZoom(1);
    setCrop({ x: 0, y: 0 });
    setCroppedAreaPixels(null);
    if (avatarCropSrc) URL.revokeObjectURL(avatarCropSrc);
    setAvatarCropSrc(file ? URL.createObjectURL(file) : null);
  };

  const onCropComplete = (_: Area, area: Area) => { setCroppedAreaPixels(area); };

  const handleAvatarUpload = async () => {
    if (!avatarFile) return;
    setAvatarUploading(true);
    setError('');
    try {
      let blob: Blob = avatarFile;
      if (avatarCropSrc && croppedAreaPixels) {
        blob = await getCroppedImageBlob(avatarCropSrc, croppedAreaPixels);
      }
      await uploadAvatar(blob);
      const freshUser = await getMe() as ProfileUser;
      setUser(freshUser);
      setFormData((prev) => ({ ...prev, avatar: freshUser.avatar }));
      setAvatarFile(null);
      if (avatarCropSrc) URL.revokeObjectURL(avatarCropSrc);
      setAvatarCropSrc(null);
      setCroppedAreaPixels(null);
      setZoom(1);
      setCrop({ x: 0, y: 0 });
    } catch (error_: unknown) {
      console.error(error_);
      setError(getApiErrorMessage(error_, 'Could not upload avatar.'));
    } finally {
      setAvatarUploading(false);
    }
  };

  const cancelAvatarEdit = () => {
    setAvatarFile(null);
    if (avatarCropSrc) URL.revokeObjectURL(avatarCropSrc);
    setAvatarCropSrc(null);
    setCroppedAreaPixels(null);
    setZoom(1);
    setCrop({ x: 0, y: 0 });
  };

  // Monthly credit usage: how many of this period's granted credits are spent.
  const cap = limits.monthly_grant;
  const usage = Math.max(0, cap - limits.monthly_balance);
  const pct = cap ? Math.min(100, Math.round((usage / cap) * 100)) : 0;

  return (
    <motion.div
      variants={listContainer}
      initial="hidden"
      animate="visible"
      className="stack-5"
    >
      {/* Header */}
      <motion.div
        variants={listItem}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
      >
        <h2 className="t-h2" style={{ margin: 0 }}>Profile</h2>
        <span className="pill">Usage: <strong style={{ fontWeight: 600 }}>{usage}</strong>&thinsp;/ {cap}</span>
      </motion.div>

      {/* Avatar */}
      <motion.section variants={pageIn} className="card card--bordered card--flat">
        <div className="row-4">
          <div className="avatar-nav avatar-nav--xl" style={{ flexShrink: 0 }}>
            {avatarPreview ? (
              <img src={avatarPreview} alt="Your avatar" />
            ) : (
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden style={{ color: 'var(--ink-muted)' }}>
                <circle cx="12" cy="8" r="4" />
                <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
              </svg>
            )}
          </div>

          <div className="stack-2">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--s-2)', alignItems: 'center' }}>
              <label htmlFor="avatar-input" className="btn btn--ghost btn--sm" style={{ cursor: 'pointer' }}>
                Choose image
              </label>
              <input id="avatar-input" type="file" accept="image/*" style={{ display: 'none' }} onChange={onAvatarPick} />
              <button
                type="button"
                className="btn btn--primary btn--sm"
                onClick={() => { void handleAvatarUpload(); }}
                disabled={!avatarFile || avatarUploading}
              >
                {avatarUploading ? 'Uploading…' : 'Save avatar'}
              </button>
              {avatarFile && (
                <button type="button" className="link t-small" onClick={cancelAvatarEdit}>
                  Cancel
                </button>
              )}
            </div>
            <p className="t-small t-mute" style={{ margin: 0 }}>JPG · PNG · WebP · up to 5 MB</p>
          </div>
        </div>

        <AnimatePresence initial={false}>
          {avatarCropSrc && (
            <motion.div
              key="cropper"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={quick}
              style={{ marginTop: 'var(--s-4)' }}
            >
              <style>{`
                .reactEasyCrop_CropArea { border: 3px solid var(--c-kivo-teal) !important; opacity: .9; }
                .reactEasyCrop_Container { background: var(--c-charcoal); }
              `}</style>
              <div className="cropper-frame" style={{ maxWidth: 520, marginInline: 'auto' }}>
                <Cropper
                  image={avatarCropSrc}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  cropShape="rect"
                  showGrid={false}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                  zoomWithScroll
                  minZoom={1}
                  maxZoom={6}
                  restrictPosition
                />
              </div>

              <div className="row-3" style={{ justifyContent: 'center', marginTop: 'var(--s-4)' }}>
                <span className="t-small t-mute">Zoom</span>
                <input
                  type="range"
                  min={1}
                  max={6}
                  step={0.01}
                  value={zoom}
                  onChange={(e) => { setZoom(Number.parseFloat(e.target.value)); }}
                  style={{ width: 'min(60vw, 320px)', accentColor: 'var(--c-kivo-teal)' }}
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.section>

      {/* Profile details */}
      <motion.section variants={pageIn} className="card card--bordered card--flat">
        <AnimatePresence mode="wait" initial={false}>
          {!editing ? (
            <motion.div
              key="view"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={quick}
              className="stack-3"
            >
              <Row label="Name" value={displayOptional(user.name)} />
              <Row label="Surname" value={displayOptional(user.surname)} />
              <Row label="Email" value={user.email} />
              <Row label="Country" value={displayOptional(user.country)} />
              <Row label="City" value={displayOptional(user.city)} />
              <Row
                label="Birthdate"
                value={
                  user.birthdate && user.birthdate !== '0001-01-01T00:00:00Z'
                    ? new Date(user.birthdate).toLocaleDateString()
                    : '—'
                }
              />
              <Row label="Gender" value={displayOptional(user.gender)} />
              {user.created_at && <Row label="Member since" value={new Date(user.created_at).toLocaleDateString()} />}

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--s-2)', paddingTop: 'var(--s-2)' }}>
                <button
                  type="button"
                  className="btn btn--primary btn--sm"
                  onClick={() => { setEditing(true); }}
                >
                  Edit profile
                </button>
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={() => { navigateTo('/plans'); }}
                >
                  Upgrade plan
                </button>
              </div>

              {error && (
                <div className="card card--error card--compact t-small" role="alert" style={{ marginTop: 'var(--s-2)' }}>
                  {error}
                </div>
              )}
            </motion.div>
          ) : (
            <motion.div
              key="edit"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={quick}
            >
              <div className="profile-edit-grid">
                {/* Name */}
                <div className="stack-2">
                  <input
                    className={`form-input${showNameErr ? ' form-input--error' : ''}`}
                    type="text"
                    name="name"
                    placeholder="First name"
                    value={formData.name ?? ''}
                    onChange={handleChange}
                    onBlur={() => { setTouched((t) => ({ ...t, name: true })); }}
                    aria-invalid={showNameErr ? 'true' : 'false'}
                  />
                  <AnimatePresence initial={false}>
                    {showNameErr && (
                      <motion.p
                        initial={{ opacity: 0, y: -4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4 }}
                        className="field-error"
                        style={{ margin: 0 }}
                      >
                        Use letters, spaces or - . &apos; (max 40).
                      </motion.p>
                    )}
                  </AnimatePresence>
                </div>

                {/* Surname */}
                <div className="stack-2">
                  <input
                    className={`form-input${showSurnameErr ? ' form-input--error' : ''}`}
                    type="text"
                    name="surname"
                    placeholder="Last name"
                    value={formData.surname ?? ''}
                    onChange={handleChange}
                    onBlur={() => { setTouched((t) => ({ ...t, surname: true })); }}
                    aria-invalid={showSurnameErr ? 'true' : 'false'}
                  />
                  <AnimatePresence initial={false}>
                    {showSurnameErr && (
                      <motion.p
                        initial={{ opacity: 0, y: -4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4 }}
                        className="field-error"
                        style={{ margin: 0 }}
                      >
                        Use letters, spaces or - . &apos; (max 40).
                      </motion.p>
                    )}
                  </AnimatePresence>
                </div>

                {/* Country */}
                <div className="stack-2">
                  <input
                    className={`form-input${showCountryErr ? ' form-input--error' : ''}`}
                    type="text"
                    name="country"
                    placeholder="Country"
                    value={formData.country ?? ''}
                    onChange={handleChange}
                    onBlur={() => { setTouched((t) => ({ ...t, country: true })); }}
                    aria-invalid={showCountryErr ? 'true' : 'false'}
                  />
                  <AnimatePresence initial={false}>
                    {showCountryErr && (
                      <motion.p
                        initial={{ opacity: 0, y: -4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4 }}
                        className="field-error"
                        style={{ margin: 0 }}
                      >
                        Use letters &amp; spaces (max 56).
                      </motion.p>
                    )}
                  </AnimatePresence>
                </div>

                {/* City */}
                <div className="stack-2">
                  <input
                    className={`form-input${showCityErr ? ' form-input--error' : ''}`}
                    type="text"
                    name="city"
                    placeholder="City"
                    value={formData.city ?? ''}
                    onChange={handleChange}
                    onBlur={() => { setTouched((t) => ({ ...t, city: true })); }}
                    aria-invalid={showCityErr ? 'true' : 'false'}
                  />
                  <AnimatePresence initial={false}>
                    {showCityErr && (
                      <motion.p
                        initial={{ opacity: 0, y: -4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4 }}
                        className="field-error"
                        style={{ margin: 0 }}
                      >
                        Use letters &amp; spaces (max 60).
                      </motion.p>
                    )}
                  </AnimatePresence>
                </div>

                {/* Birthdate */}
                <div className="stack-2">
                  <label htmlFor="birthdate" className="t-small t-mute">Birthdate</label>
                  {isIOS ? (
                    <input
                      id="birthdate"
                      className={`form-input${showDobErr ? ' form-input--error' : ''}`}
                      type="text"
                      name="birthdate"
                      placeholder="YYYY-MM-DD"
                      inputMode="numeric"
                      pattern="\d{4}-\d{2}-\d{2}"
                      value={birthdateInput}
                      onChange={(e) => {
                        const next = formatYMD(e.target.value);
                        setFormData((prev) => ({ ...prev, birthdate: next }));
                      }}
                      onBlur={() => { setTouched((t) => ({ ...t, birthdate: true })); }}
                      autoComplete="bday"
                      aria-invalid={showDobErr ? 'true' : 'false'}
                      aria-describedby={showDobErr ? 'dob-error' : 'dob-hint'}
                    />
                  ) : (
                    <input
                      id="birthdate"
                      className={`form-input${showDobErr ? ' form-input--error' : ''}`}
                      type="date"
                      name="birthdate"
                      value={birthdateInput}
                      onChange={handleChange}
                      onBlur={() => { setTouched((t) => ({ ...t, birthdate: true })); }}
                      autoComplete="bday"
                      max={todayISO}
                      aria-invalid={showDobErr ? 'true' : 'false'}
                      aria-describedby={showDobErr ? 'dob-error' : 'dob-hint'}
                    />
                  )}
                  <AnimatePresence initial={false} mode="wait">
                    {showDobErr ? (
                      <motion.p
                        key="err"
                        id="dob-error"
                        initial={{ opacity: 0, y: -4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4 }}
                        className="field-error"
                        style={{ margin: 0 }}
                      >
                        Please use a valid date (YYYY-MM-DD).
                      </motion.p>
                    ) : (
                      <motion.p
                        key="hint"
                        id="dob-hint"
                        initial={{ opacity: 0, y: -4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4 }}
                        className="t-small t-mute"
                        style={{ margin: 0 }}
                      >
                        Format: <span style={{ fontVariantNumeric: 'tabular-nums' }}>YYYY-MM-DD</span>
                      </motion.p>
                    )}
                  </AnimatePresence>
                </div>

                {/* Gender */}
                <div className="stack-2" style={{ gridColumn: '1 / -1' }}>
                  <label htmlFor="gender-select" className="t-small t-mute">Gender (optional)</label>
                  <div style={{ position: 'relative' }}>
                    <select
                      id="gender-select"
                      value={genderOption}
                      onChange={(e) => {
                        const v = e.target.value as GenderOption;
                        setGenderOption(v);
                        if (v !== 'Other') setGenderOtherText('');
                      }}
                      className="form-input"
                      style={{ appearance: 'none', paddingRight: 'var(--s-7)', color: genderOption ? 'var(--ink)' : 'var(--ink-muted)' }}
                      aria-label="Select your gender (optional)"
                    >
                      <option value="">— Select —</option>
                      <option value="Female">Female</option>
                      <option value="Male">Male</option>
                      <option value="Prefer not to say">Prefer not to say</option>
                      <option value="Other">Other</option>
                    </select>
                    <svg
                      style={{ position: 'absolute', right: 'var(--s-3)', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: 'var(--ink-muted)' }}
                      width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden
                    >
                      <path d="M6 9l6 6 6-6" />
                    </svg>
                  </div>

                  <AnimatePresence initial={false}>
                    {genderOption === 'Other' && (
                      <motion.div
                        key="gender-other"
                        initial={{ opacity: 0, y: -4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4 }}
                        transition={quick}
                        className="stack-2"
                      >
                        <input
                          className={`form-input${showGenderOtherErr ? ' form-input--error' : ''}`}
                          type="text"
                          placeholder="Enter gender (optional)"
                          value={genderOtherText}
                          onChange={(e) => { setGenderOtherText(e.target.value); }}
                          onBlur={() => { setTouched((t) => ({ ...t, genderOther: true })); }}
                          aria-invalid={showGenderOtherErr ? 'true' : 'false'}
                        />
                        <AnimatePresence initial={false}>
                          {showGenderOtherErr && (
                            <motion.p
                              initial={{ opacity: 0, y: -4 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -4 }}
                              className="field-error"
                              style={{ margin: 0 }}
                            >
                              1–40 chars; letters, numbers, spaces and - . , &apos; / allowed.
                            </motion.p>
                          )}
                        </AnimatePresence>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>

              {error && (
                <div className="card card--error card--compact t-small" role="alert" style={{ marginTop: 'var(--s-3)' }}>
                  {error}
                </div>
              )}

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--s-2)', marginTop: 'var(--s-4)' }}>
                <button
                  type="button"
                  className="btn btn--primary btn--sm"
                  onClick={() => { void handleSave(); }}
                  disabled={saving || !allValid}
                >
                  {saving ? 'Saving…' : 'Save'}
                </button>
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  disabled={saving}
                  onClick={() => {
                    setEditing(false);
                    setFormData(user);
                    setTouched({ name: false, surname: false, country: false, city: false, birthdate: false, genderOther: false });
                    initGenderFromString(user.gender ?? '', setGenderOption, setGenderOtherText);
                    cancelAvatarEdit();
                  }}
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.section>

      {/* Account & security */}
      <motion.section variants={pageIn} className="card card--bordered card--flat">
        <div className="stack-4">
          <div className="stack-2">
            <h3 className="t-h3" style={{ margin: 0 }}>Account &amp; security</h3>
            <p className="t-small t-mute" style={{ margin: 0 }}>
              Forgot your password? We&apos;ll email a reset link to{' '}
              <span style={{ fontFamily: 'ui-monospace, monospace', wordBreak: 'break-all' }}>{user.email}</span>.
            </p>
          </div>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => { void handleResetPassword(); }}
            style={{ alignSelf: 'flex-start' }}
          >
            Reset password
          </button>
        </div>
      </motion.section>

      {/* Privacy */}
      <motion.section variants={pageIn} className="card card--bordered card--flat">
        <div className="stack-4">
          <h3 className="t-h3" style={{ margin: 0 }}>Privacy</h3>

          <label style={{ display: 'flex', gap: 'var(--s-3)', cursor: 'pointer', alignItems: 'flex-start' }}>
            <input
              type="checkbox"
              checked={photoAiConsent}
              onChange={(e) => { void handlePrivacyToggle('photo_ai_consent', e.target.checked); }}
              className="sr-only"
            />
            <span className={`kv-checkbox${photoAiConsent ? ' kv-checkbox--on' : ''}`} aria-hidden="true">
              <svg viewBox="0 0 12 10" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" width={10} height={9}>
                <path d="M1 5l3 3 7-7" />
              </svg>
            </span>
            <span className="t-small">Allow use of my photos to improve the AI</span>
          </label>

          <label style={{ display: 'flex', gap: 'var(--s-3)', cursor: 'pointer', alignItems: 'flex-start' }}>
            <input
              type="checkbox"
              checked={newsletterOptIn}
              onChange={(e) => { void handlePrivacyToggle('newsletter_opt_in', e.target.checked); }}
              className="sr-only"
            />
            <span className={`kv-checkbox${newsletterOptIn ? ' kv-checkbox--on' : ''}`} aria-hidden="true">
              <svg viewBox="0 0 12 10" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" width={10} height={9}>
                <path d="M1 5l3 3 7-7" />
              </svg>
            </span>
            <span className="t-small">Receive email updates and newsletters</span>
          </label>

          <label style={{ display: 'flex', gap: 'var(--s-3)', cursor: 'pointer', alignItems: 'flex-start' }}>
            <input
              type="checkbox"
              checked={affiliateRecs}
              onChange={(e) => { handleAffiliateToggle(e.target.checked); }}
              className="sr-only"
            />
            <span className={`kv-checkbox${affiliateRecs ? ' kv-checkbox--on' : ''}`} aria-hidden="true">
              <svg viewBox="0 0 12 10" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" width={10} height={9}>
                <path d="M1 5l3 3 7-7" />
              </svg>
            </span>
            <span className="t-small">
              Show affiliate-linked product recommendations. Some links may earn us a small
              commission at no extra cost to you — see the{' '}
              <a href="/affiliate-disclosure" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--c-kivo-teal)', textDecoration: 'underline', textUnderlineOffset: 2 }}>
                Affiliate Disclosure
              </a>.
            </span>
          </label>
        </div>
      </motion.section>

      {/* Push & in-app notification preferences */}
      <NotificationSettings variants={pageIn} />

      {/* Data & privacy */}
      <motion.section variants={pageIn} className="card card--bordered card--flat">
        <div className="stack-4">
          <div className="stack-2">
            <h3 className="t-h3" style={{ margin: 0 }}>Data &amp; privacy</h3>
            <p className="t-small t-mute" style={{ margin: 0 }}>
              Download a copy of your personal data or request account deletion.
            </p>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--s-2)' }}>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => { setExportDialogOpen(true); }}
            >
              Request data export
            </button>
            <button
              type="button"
              className="btn btn--danger btn--sm"
              onClick={() => { navigateTo('/delete-account'); }}
            >
              Delete account
            </button>
          </div>
        </div>
      </motion.section>

      {/* Plan & credits */}
      <motion.section variants={pageIn} className="card card--bordered card--flat">
        <div className="stack-4">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div className="stack-2">
              <span className="t-label">Plan</span>
              <span className="t-small" style={{ fontWeight: 500 }}>{limits.plan_name}</span>
            </div>
          </div>
          <div className="stack-2">
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span className="t-small t-mute">Monthly credits</span>
              <span className="t-small t-mute">{usage} / {cap}</span>
            </div>
            <div className="plan-usage-bar" style={{ width: '100%', maxWidth: '100%' }}>
              <motion.div
                className="plan-usage-bar__fill"
                initial={{ width: 0 }}
                animate={{ width: `${pct}%` }}
                transition={spring}
              />
            </div>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--s-2)' }}>
            <button
              type="button"
              className="btn btn--primary btn--sm"
              onClick={() => { navigateTo('/wallet'); }}
            >
              Buy credits
            </button>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => { navigateTo('/plans'); }}
            >
              Manage plan
            </button>
          </div>
        </div>
      </motion.section>

      {/* Session */}
      <motion.section variants={pageIn} className="card card--bordered card--flat">
        <div className="stack-3">
          <div className="stack-1">
            <h3 className="t-h3" style={{ margin: 0 }}>Session</h3>
            <p className="t-small t-mute" style={{ margin: 0 }}>
              Sign out of Kivo on this device.
            </p>
          </div>
          <button
            type="button"
            className="btn btn--sm"
            onClick={() => { void handleLogout(); }}
            data-testid="profile-logout"
            style={{
              alignSelf: 'flex-start',
              background: 'rgba(196,103,58,0.1)',
              color: 'var(--c-error)',
            }}
          >
            Log out
          </button>
        </div>
      </motion.section>

      <Toast
        open={toast.open}
        onOpenChange={(open) => { setToast((t) => ({ ...t, open })); }}
        variant={toast.variant}
        title={toast.title}
        description={toast.description}
        placement="center"
        autoCloseMs={4000}
      />

      <ConfirmationDialog
        open={exportDialogOpen}
        onClose={() => { setExportDialogOpen(false); }}
        title="Request data export"
        description="Are you sure you want to request a data export? You will receive an email when your data is ready to download. Note that this action can only be performed once a month."
        confirmLabel="Request export"
        loading={isExporting}
        onConfirm={async () => {
          setIsExporting(true);
          try {
            await requestDataExport(user.id);
            setToast({
              open: true,
              variant: 'success',
              title: 'Export requested',
              description: 'You will receive an email when your data is ready to download.',
            });
          } catch (error_: unknown) {
            setToast({
              open: true,
              variant: 'error',
              title: 'Export request failed',
              description: getApiErrorMessage(error_, 'Could not request data export. Please try again later.'),
            });
          } finally {
            setIsExporting(false);
            setExportDialogOpen(false);
          }
        }}
      />

      <ConfirmationDialog
        open={privacyConfirmOpen}
        onClose={() => { setPrivacyConfirmOpen(false); setPrivacyConfirmField(null); }}
        title={privacyConfirmField === 'photo_ai_consent' ? 'Disable photo AI improvement?' : 'Unsubscribe from newsletter?'}
        description={privacyConfirmField === 'photo_ai_consent'
          ? 'Your photos will no longer be used to improve our AI models.'
          : 'You will no longer receive email updates and newsletters.'}
        confirmLabel="Confirm"
        onConfirm={() => { void handlePrivacyConfirm(); }}
      />
    </motion.div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--s-5)' }}>
      <span className="t-small t-mute">{label}</span>
      <span className="t-small" style={{ fontWeight: 500, textAlign: 'right' }}>{value}</span>
    </div>
  );
}
